import { Gauge, ArrowUpRight, ExternalLink, Building2, Factory } from "lucide-react";

export default function PayPerUse() {
  return (
    <section
      id="pay-per-use"
      data-testid="payperuse-section"
      className="section-padding bg-white border-b border-black/10"
    >
      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Säule 02 · Spezialthema</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Pay-per-Use
              <br />
              im <span className="text-[#D30000]">Leasing.</span>
            </h2>
            <div className="mt-6 text-xs uppercase tracking-[0.2em] text-[#4A4A52]">
              Für Firmenkunden­berater in Sparkassen & Banken · für Unternehmen direkt
            </div>

            <div className="mt-10 w-14 h-14 border border-black/15 flex items-center justify-center">
              <Gauge size={22} strokeWidth={1.5} />
            </div>

            <div className="mt-10 border border-black/10 p-6 max-w-md">
              <div className="text-xs uppercase tracking-[0.2em] text-[#D30000] mb-3">
                Kooperation
              </div>
              <div className="font-display font-bold text-xl tracking-tight">
                Linxfour · Wien
              </div>
              <p className="mt-3 text-sm text-[#4A4A52] leading-relaxed">
                Ich arbeite konkret mit der Wiener Leasing­gesellschaft
                Linxfour zusammen und bringe deren Pay-per-Use-Dienst­leistung
                in den deutschen Markt — sowohl über Firmenkunden­berater in
                Sparkassen und Banken als auch direkt zu den Unternehmen.
              </p>
              <a
                href="https://www.linxfour.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="linxfour-link"
                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold hover-marker"
              >
                linxfour.com <ExternalLink size={14} />
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A52] max-w-2xl">
              Pay-per-Use ist ein Evolutions­schritt in der Leasing­finanzierung.
              Statt einer festen Rate zahlt der Nutzer nur für die tatsächliche
              Nutzung des Wirtschafts­guts — pro Stunde, pro Kilometer, pro
              Prozess.
            </p>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-[#4A4A52] max-w-2xl">
              Geeignete Objekte sind z. B. Produktions­maschinen aller Branchen,
              Verpackungs­technologie, logistische Anlagen, Nutzfahrzeuge (auch
              E-NFZ), Waschanlagen, Bau­maschinen im Hoch- und Tiefbau. Diverse
              andere Technologien, die vernetzt arbeiten und remote die
              Nutzungs­daten darstellen können.
            </p>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-[#4A4A52] max-w-2xl">
              Leasing­gesellschaft und Leasing­nehmer stimmen sich bei den
              Vertrags­verhandlungen eng über die zu erwartende Nutzung ab und
              legen gemeinsam die Parameter über die Vertrags­laufzeit fest.
            </p>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-[#4A4A52] max-w-2xl">
              Die Liquiditäts­belastung für den Leasing­vertrag verläuft analog
              dem Auslastungs­grad des Objekts. Hier entsteht ein echter
              <span className="font-medium text-[#0A0A0A]"> Pay-as-you-earn-Effekt</span> —
              sowohl in der Liquiditäts­position als auch in der Gewinn- und
              Verlust­rechnung (G&amp;V). Durch diese Konstellation ergibt sich
              in HGB und IFRS ein echter
              <span className="font-medium text-[#0A0A0A]"> Off-Balance-Effekt</span>.
            </p>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-[#4A4A52] max-w-2xl">
              Linxfour trägt bei unkalkulierter niedriger Auslastung gemeinsam mit dem
              Unternehmer ein Ertragsrisiko.
            </p>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-[1px] bg-black/10 border border-black/10">
              <Channel
                icon={Building2}
                label="Kanal A"
                title="Über Firmenkunden­berater"
                text="Sparkassen und Banken erweitern ihr Angebot: Pay-per-Use als zusätzliche Finanzierungs­lösung im Firmenkunden­gespräch — vom Kundenkonzept bis zur Umsetzung."
              />
              <Channel
                icon={Factory}
                label="Kanal B"
                title="Direkt an Unternehmen"
                text="Mittel­ständische Unternehmen erhalten Pay-per-Use als flexible Finanzierungs­alternative — mit Beratung entlang ihrer Investitions- und Nutzungs­realität."
              />
            </div>

            <a
              href="#kontakt"
              className="mt-10 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] hover-marker w-fit"
              data-testid="payperuse-cta"
            >
              Erstgespräch zu Pay-per-Use <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Channel({ icon: Icon, label, title, text }) {
  return (
    <div className="bg-white p-6 md:p-8">
      <div className="flex items-start justify-between mb-6">
        <div className="w-10 h-10 border border-black/15 flex items-center justify-center">
          <Icon size={18} strokeWidth={1.5} />
        </div>
        <span className="text-xs font-mono opacity-50">{label}</span>
      </div>
      <h4 className="font-display font-bold text-lg md:text-xl tracking-tight">
        {title}
      </h4>
      <p className="mt-3 text-sm leading-relaxed text-[#4A4A52]">{text}</p>
    </div>
  );
}
